import { ReactNode } from 'react';
import { getIsMounted, pushStackPage } from '@ardoq/stack-page-manager';
import { ModalContainer } from './ModalContainer';

export type ResolvableBoolean = boolean | (() => boolean | Promise<boolean>);

export type ModalOptions = {
  autoFocus?: boolean;
  /**
   * A boolean or a function that returns a boolean (or `Promise<boolean>`). If the modal should close.
   */
  shouldCloseOnBackdropClick?: ResolvableBoolean;
  /**
   * A boolean or a function that returns a boolean (or `Promise<boolean>`). If the modal should close.
   */
  shouldCloseOnEscapeKey?: ResolvableBoolean;
  /**
   * A function that will be called when the modal is closed, regardless of method.
   */
  onClose?: () => void;
};

export type ModalResolveFn<T = unknown> = (resultValue: T | false) => void;

export const resolveBoolean = async (selector: ResolvableBoolean) => {
  if (typeof selector === 'function') {
    return await selector();
  }
  return selector;
};

/**
 * Use this function when you would like to launch a custom built dialog from
 * anywhere in the app. The function is async so it should be treated as that.
 * Returns a promise that will resolve with `T` or `false` based on the user
 * input.
 * @async
 * @param content A function that returns a `ReactNode`.
 * @deprecated Avoid this in new code. Use `AnimatedModal` and control `isOpen`
 * like any other state. See #19797
 */
export async function modal<T>(
  content: (resolve: ModalResolveFn<T>) => ReactNode,
  {
    shouldCloseOnBackdropClick = true,
    shouldCloseOnEscapeKey = true,
    autoFocus = true,
    onClose,
  }: ModalOptions = {}
): Promise<T | false> {
  await getIsMounted();
  const result = new Promise<T | false>(resolve => {
    pushStackPage(
      ({ closeStackPage, stackPageIsInFocus }) => {
        const handleResolve = async (result: T | false) => {
          await closeStackPage();
          if (onClose) onClose();
          resolve(result);
        };

        return (
          <ModalContainer
            onEscPress={async () => {
              if (!stackPageIsInFocus) {
                return;
              }
              return (
                (await resolveBoolean(shouldCloseOnEscapeKey)) &&
                handleResolve(false)
              );
            }}
            onBackdropClick={async () =>
              (await resolveBoolean(shouldCloseOnBackdropClick)) &&
              handleResolve(false)
            }
          >
            {content(handleResolve)}
          </ModalContainer>
        );
      },
      {
        autoFocus,
      }
    );
  });

  return result;
}
